import React from 'react';
import Footer from './Footer';

const EbookLanding: React.FC = () => {
  const handlePurchase = (format: string) => {
    // Redirect to payment page with selected format
    window.location.href = `/payment?format=${format}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-amber-50 to-sky-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center h-16">
            <button 
              onClick={() => handlePurchase('pdf')}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-amber-500 text-white rounded-md hover:from-blue-600 hover:to-amber-600 transition-all"
            >
              KÚPIŤ
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Prestaň sa báť
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-amber-600">
                  otvoriť internet banking
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Jednoduchý systém, vďaka ktorému získaš prehľad, rezervu a pokoj vo financiách — bez tabuliek a stresu.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => handlePurchase('pdf')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-amber-500 text-white font-semibold rounded-md shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-lg"
                >
                  👉 Chcem finančný pokoj
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-amber-300 rounded-md transform rotate-6 opacity-20"></div>
                <div className="relative bg-gradient-to-br from-blue-100 to-amber-100 rounded-md p-8 shadow-2xl">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Keď peniaze prestanú strašiť</h3>
                    <p className="text-gray-600">Kompletný systém pre tvoje financie</p>
                    <div className="mt-6 pt-6 border-t border-blue-200">
                      <p className="text-3xl font-bold text-blue-600">5,99 €</p>
                      <p className="text-sm text-gray-500">Jednorázovo</p>
                      <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                        Tento e-book je určený na vzdelávanie a zdieľanie informácií o osobných financiách. Nejde o individuálne finančné poradenstvo. Každá finančná situácia je jedinečná, preto si pred dôležitými rozhodnutiami zváž konzultáciu s odborníkom.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scrollable Cards Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Čo sa naučíš
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-amber-400 mx-auto rounded-md"></div>
          </div>
          
          {/* Horizontal Scroll Container */}
          <div className="relative">
            <div className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth">
              {[
                { icon: "🎯", title: "Presná kontrola", desc: "Zisti presne koľko môžeš minúť", color: "blue" },
                { icon: "💰", title: "Prvá rezerva", desc: "Odlož si prvých 500 € bez stresu", color: "amber" },
                { icon: "🔄", title: "Konečný cyklus", desc: "Prestaň žiť od výplaty k výplate", color: "blue" },
                { icon: "✅", title: "Jednoduchý systém", desc: "Žiadne tabuľky, žiadne počítanie", color: "amber" },
                { icon: "📊", title: "Prehľad za 10 minút", desc: "Rýchla kontrola financií každý deň", color: "blue" },
                { icon: "🧠", title: "Psychológia míňania", desc: "Prekonaj impulzívne nákupy", color: "amber" },
                { icon: "🎯", title: "Ciele a sny", desc: "Aplikuj systém na svoje ciele", color: "blue" },
                { icon: "💡", title: "Finančná inteligencia", desc: "Rozumej peniazom raz a navždy", color: "amber" }
              ].map((card, index) => (
                <div key={index} className={`flex-shrink-0 w-80 p-6 rounded-md bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 ${
                  card.color === 'blue' ? 'border-blue-200' : 'border-amber-200'
                }`}>
                  <div className={`w-16 h-16 rounded-md flex items-center justify-center text-3xl mb-4 ${
                    card.color === 'blue' 
                      ? 'bg-gradient-to-br from-blue-100 to-blue-200' 
                      : 'bg-gradient-to-br from-amber-100 to-amber-200'
                  }`}>
                    {card.icon}
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${
                    card.color === 'blue' ? 'text-blue-800' : 'text-amber-800'
                  }`}>
                    {card.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Získaj prístup k ebooku po platbe
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-amber-400 mx-auto rounded-md"></div>
            <p className="text-lg text-gray-600 mt-6">
              Bezpečná platba cez Viva Wallet, okamžitý prístup k ebooku
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* PDF Format */}
            <div className="bg-white rounded-md shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center border-2 border-blue-200">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                📄
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">PDF</h3>
              <p className="text-gray-600 mb-6">
                Pre tlač a čítanie na počítači. 30 strán.
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-2">
                <li>✅ Ideálne pre tlač</li>
                <li>✅ Kompatibilné s PC/Mac</li>
                <li>✅ Pevné formátovanie</li>
              </ul>
              <button 
                onClick={() => handlePurchase('pdf')}
                className="inline-block w-full px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-md hover:from-red-600 hover:to-red-700 transition-all duration-200"
              >
                Kúpiť PDF - 5,99 €
              </button>
            </div>

            {/* EPUB Format */}
            <div className="bg-white rounded-md shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center border-2 border-amber-200">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                📱
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">EPUB</h3>
              <p className="text-gray-600 mb-6">
                Pre mobilné zariadenia a tablety.
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-2">
                <li>✅ Prispôsobiteľné písmo</li>
                <li>✅ Ideálne pre telefóny</li>
                <li>✅ Reflowable text</li>
              </ul>
              <button 
                onClick={() => handlePurchase('epub')}
                className="inline-block w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-md hover:from-green-600 hover:to-green-700 transition-all duration-200"
              >
                Kúpiť EPUB - 5,99 €
              </button>
            </div>

            {/* MOBI Format */}
            <div className="bg-white rounded-md shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center border-2 border-purple-200">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                📚
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">MOBI</h3>
              <p className="text-gray-600 mb-6">
                Pre Kindle a Amazon čítačky.
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-2">
                <li>✅ Kompatibilné s Kindle</li>
                <li>✅ Optimalizované pre e-ink</li>
                <li>✅ Amazon zariadenia</li>
              </ul>
              <button 
                onClick={() => handlePurchase('mobi')}
                className="inline-block w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-md hover:from-purple-600 hover:to-purple-700 transition-all duration-200"
              >
                Kúpiť MOBI - 5,99 €
              </button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-blue-100 to-amber-100 rounded-md p-6 border border-blue-200">
              <p className="text-lg font-semibold text-gray-900 mb-2">
                🎁 Všetky formáty v jednej cene
              </p>
              <p className="text-gray-600">
                Po platbe 5,99 € získate prístup ku všetkým trom formátom
              </p>
              <div className="mt-4 flex justify-center items-center gap-4">
                <span className="text-sm text-gray-500">Bezpečná platba:</span>
                <div className="flex gap-2">
                  <span className="text-2xl">💳</span>
                  <span className="text-2xl">🔒</span>
                  <span className="text-2xl">✅</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EbookLanding;
